import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import {
  getOrganizationCoursesUserCountQuery,
  getUserCountByEntityandEntityIdsQuery,
} from "../modules/CoursesQueries";
import { DEBOUNCE_MS } from "../modules/CoursesConstants";
import _ from "lodash";

const EMPTY_ARRAY = [];

/**
 * Fetches entity counts at two levels:
 * 1. Course-level: student, staff, family counts per visible course (debounced, ref-cached)
 * 2. All-schools aggregate: total counts across all schools (for collapsed trigger subtitle)
 *
 * @param {Object} params
 * @param {string[]} params.visibleCourseIds - Currently visible course IDs
 * @param {string} params.organizationId - Organization ID
 * @param {string[]} params.allSchoolIds - All school IDs from hierarchy
 * @param {boolean} params.viewOnly - Skip fetching if true
 * @returns {{ courseCountMap: Object, allSchoolsCount: Object, loading: boolean }}
 */
const useEntityCounts = ({
  visibleCourseIds,
  organizationId,
  allSchoolIds,
  viewOnly,
  selectedSchoolIds,
  isActive = true,
}) => {
  // --- Course-level counts (debounced + cached) ---
  const courseCountMapRef = useRef({});
  const previousCourseIdsRef = useRef(EMPTY_ARRAY);
  const [debouncedVisibleIds, setDebouncedVisibleIds] = useState(
    visibleCourseIds
  );
  const [preLoading, setPreLoading] = useState(false);
  const [countVersion, setCountVersion] = useState(0);

  useEffect(() => {
    setPreLoading(true);
    const timeoutId = setTimeout(() => {
      setPreLoading(false);
      setDebouncedVisibleIds(visibleCourseIds);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCourseIds.join(",")]);

  const courseIdsToFetch = useMemo(() => {
    if (_.isEmpty(debouncedVisibleIds)) return EMPTY_ARRAY;

    const idsToFetch = _.filter(
      debouncedVisibleIds,
      id => !courseCountMapRef.current[id]
    );

    const sortedIds = _.sortBy(idsToFetch);

    if (_.isEqual(sortedIds, previousCourseIdsRef.current)) {
      return previousCourseIdsRef.current;
    }

    previousCourseIdsRef.current = sortedIds;
    return sortedIds;
  }, [debouncedVisibleIds]);

  const { data: courseCountData, loading: courseCountLoading } = useQuery(
    getOrganizationCoursesUserCountQuery,
    {
      variables: { courseIds: courseIdsToFetch },
      context: { useHttpLink: true },
      skip: _.isEmpty(courseIdsToFetch) || viewOnly,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
      errorPolicy: "all",
    }
  );

  useEffect(() => {
    const multiNodeData = _.get(courseCountData, "multiNode", []);

    if (_.isEmpty(multiNodeData)) return;

    let hasNewData = false;
    _.forEach(multiNodeData, course => {
      if (course.id && !courseCountMapRef.current[course.id]) {
        courseCountMapRef.current[course.id] = course;
        hasNewData = true;
      }
    });

    if (hasNewData) {
      setCountVersion(prev => prev + 1);
    }
  }, [courseCountData]);

  const courseCountMap = useMemo(() => ({ ...courseCountMapRef.current }), [
    countVersion,
  ]);

  // null selectedSchoolIds = "all schools" sentinel — fall back to full allSchoolIds list.
  const effectiveSchoolIdsForCountQuery = selectedSchoolIds
    ? [...selectedSchoolIds]
    : allSchoolIds;

  // --- All-schools aggregate counts (for collapsed trigger subtitle) ---
  const {
    data: allSchoolsCountData,
    loading: allSchoolsCountLoading,
  } = useQuery(getUserCountByEntityandEntityIdsQuery, {
    variables: {
      organizationId,
      input: {
        entity: "CURRICULUM_PROGRAM",
        entityIds: effectiveSchoolIdsForCountQuery,
      },
    },
    context: { useHttpLink: true },
    skip:
      _.isEmpty(effectiveSchoolIdsForCountQuery) ||
      !organizationId ||
      viewOnly ||
      !isActive,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  const allSchoolsCount = useMemo(
    () => _.get(allSchoolsCountData, "node.users.userCountByEntity", {}),
    [allSchoolsCountData]
  );

  return {
    courseCountMap,
    allSchoolsCount,
    loading:
      (courseCountLoading && _.isEmpty(courseCountData)) ||
      preLoading ||
      (allSchoolsCountLoading && _.isEmpty(allSchoolsCountData)),
    allSchoolsCountLoading:
      allSchoolsCountLoading && _.isEmpty(allSchoolsCountData),
  };
};

export default useEntityCounts;
